import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Loader2, Ruler, Satellite, CheckCircle2, Upload, FileText } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface MeasurementResult {
  measurementId?: string;
  estimateId?: string;
  totalSquares?: number;
  orderSquares?: number;
  customerPrice?: number;
  lineItemCount?: number;
  manualRequired?: boolean;
  message?: string;
  lat?: number;
  lng?: number;
}

interface JobMeasureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  address: string;
  measuring?: boolean;
  result: MeasurementResult | null;
  onRunSatellite: (address: string, manualSquares?: number, lat?: number | null, lng?: number | null) => Promise<void>;
  onOpenEstimate: () => void;
  jobId?: string;
  orgId?: string;
  tradeCode?: string;
  tradeId?: string;
  onUploadSuccess?: () => void;
}

export function JobMeasureDialog({
  open,
  onOpenChange,
  address,
  measuring = false,
  result,
  onRunSatellite,
  onOpenEstimate,
  jobId,
  orgId,
  tradeCode = "roofing",
  tradeId,
  onUploadSuccess,
}: JobMeasureDialogProps) {
  const [measurementAddress, setMeasurementAddress] = useState(address);
  const [manualSquares, setManualSquares] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayAddress = measurementAddress || address;
  const approvedSquares = Number(manualSquares);

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) {
      toast.error("Please upload a PDF measurement report");
      event.target.value = "";
      return;
    }
    if (!jobId || !orgId) {
      toast.error("Missing job context — try reopening this dialog");
      event.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const r = reader.result as string;
          resolve(r.split(",")[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { error } = await supabase.functions.invoke("parse-measurement-report", {
        body: {
          file_data: base64,
          file_name: file.name,
          job_id: jobId,
          trade_id: tradeId,
          trade_code: tradeCode,
          org_id: orgId,
        },
      });
      if (error) throw error;
      toast.success("Measurement report parsed and saved");
      onUploadSuccess?.();
      onOpenChange(false);
    } catch (err: any) {
      console.error("Upload measurement error:", err);
      toast.error(err.message || "Failed to parse measurement report");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ruler className="h-5 w-5 text-primary" />
            Run Job Measurement
          </DialogTitle>
          <DialogDescription>
            Measure this roof first, then apply the measurement directly to a draft estimate.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="measurement-address">Measurement address</Label>
            <Input
              id="measurement-address"
              value={measurementAddress}
              onChange={(event) => setMeasurementAddress(event.target.value)}
              placeholder="Enter the property address"
            />
          </div>

          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Satellite className="h-4 w-4 text-primary" />
                  <h3 className="font-semibold">Satellite roof measurement</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Pulls roof squares, ridges, hips, valleys, eaves, and rakes, then creates the job estimate from it.
                </p>
              </div>
              <Button
                onClick={() => onRunSatellite(displayAddress)}
                disabled={measuring || !displayAddress.trim()}
                className="shrink-0 gap-2"
              >
                {measuring ? <Loader2 className="h-4 w-4 animate-spin" /> : <Satellite className="h-4 w-4" />}
                {measuring ? "Measuring" : "Measure Roof"}
              </Button>
            </div>
          </div>

          <div className="relative flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">or use your own report</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <h3 className="font-semibold">Upload measurement report</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Already have a report? Upload a PDF from EagleView, Roofr, Hover, GAF QuickMeasure, RoofSnap, or any other provider — we'll parse squares, ridges, hips, valleys, eaves, and rakes automatically.
                </p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {["EagleView", "Roofr", "Hover", "GAF QuickMeasure", "RoofSnap", "Other PDF"].map((s) => (
                    <Badge key={s} variant="outline" className="text-[10px] font-normal">{s}</Badge>
                  ))}
                </div>
              </div>
              <Button
                variant="outline"
                onClick={openFilePicker}
                disabled={uploading || !jobId || !orgId}
                className="shrink-0 gap-2"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Parsing..." : "Upload PDF"}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={handleUpload}
                className="hidden"
              />
            </div>
          </div>

          {result && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  {result.manualRequired ? "Manual measurement needed" : "Measurement saved and applied"}
                </div>
                <Badge variant="secondary">{result.manualRequired ? "No satellite coverage" : "Draft estimate ready"}</Badge>
              </div>

              {result.manualRequired ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">{result.message}</p>
                  <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                    <div className="space-y-2">
                      <Label htmlFor="manual-squares">Approved roof squares</Label>
                      <Input
                        id="manual-squares"
                        type="number"
                        min="0"
                        step="0.01"
                        value={manualSquares}
                        onChange={(event) => setManualSquares(event.target.value)}
                        placeholder="Enter hand-measured squares"
                      />
                    </div>
                    <Button
                      onClick={() => onRunSatellite(displayAddress, approvedSquares, result.lat ?? null, result.lng ?? null)}
                      disabled={measuring || !approvedSquares || approvedSquares <= 0}
                      className="gap-2"
                    >
                      {measuring ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Approve & Create
                    </Button>
                  </div>
                </div>
              ) : (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <div className="rounded-md bg-background p-3">
                  <p className="text-xs text-muted-foreground">Measured</p>
                  <p className="text-lg font-semibold">{(result.totalSquares ?? 0).toFixed(2)} sq</p>
                </div>
                <div className="rounded-md bg-background p-3">
                  <p className="text-xs text-muted-foreground">Order</p>
                  <p className="text-lg font-semibold">{result.orderSquares ?? 0} sq</p>
                </div>
                <div className="rounded-md bg-background p-3">
                  <p className="text-xs text-muted-foreground">Estimate</p>
                  <p className="text-lg font-semibold">${Math.round(result.customerPrice ?? 0).toLocaleString()}</p>
                </div>
                <div className="rounded-md bg-background p-3">
                  <p className="text-xs text-muted-foreground">Items</p>
                  <p className="text-lg font-semibold">{result.lineItemCount ?? 0}</p>
                </div>
              </div>
              )}
            </div>
          )}
        </div>

        <Separator />

        <DialogFooter className="gap-2 sm:justify-between">
          <p className="text-xs text-muted-foreground sm:mr-auto">
            This creates a saved measurement record and links it to the job estimate.
          </p>
          {result && !result.manualRequired && (
            <Button onClick={onOpenEstimate} className="gap-2">
              Open Estimate
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}